
import { defineComponent, reactive, toRefs, onMounted, onActivated } from 'vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import { EmployeePageParamModel, EmployeeTableViewModel } from '@/model/master-data/employeeModel'
import { useRouter } from 'vue-router'
import { getEmployeePageList, exportEmployeeTemplate, excelSupplierFileUpload } from '@/apis/main-data/employee'
import { ElMessage } from 'element-plus'
import { useStore } from 'vuex'
import { RootObject } from '@/model/rootObject'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'EmployeePageList',
  components: {
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const searchStruct = [
      { prop: 'nameCn', label: t('employee.nameCn'), component: 'el-input', type: 'text', model: '' },
      { prop: 'nameEn', label: t('employee.nameEn'), component: 'el-input', type: 'text', model: '' },
      { prop: 'employeeNumber', label: t('employee.employeeId'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      {
        prop: 'isDelete',
        label: t('common.deleteStatus'),
        component: 'el-select',
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      }
    ]
    const employeeTableStruct = [
      { prop: 'index', label: t('common.indexNumber') },
      { prop: 'nameCn', label: t('employee.nameCn') },
      { prop: 'nameEn', label: t('employee.nameEn') },
      { prop: 'employeeNumber', label: t('employee.employeeId') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'mail', label: t('employee.email') },
      { prop: 'isDelete', label: t('common.deleteStatus') }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as EmployeePageParamModel,
      tableData: Array<EmployeeTableViewModel>(),
      columnStruct: employeeTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        const params: EmployeePageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          employeeNumber: dataMap.searchFormModel.employeeNumber,
          isDelete: dataMap.searchFormModel.isDelete,
          nameCn: dataMap.searchFormModel.nameCn,
          nameEn: dataMap.searchFormModel.nameEn,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getEmployeePageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list.map(
            (item, index) => ({
              id: item.id,
              index: index + 1,
              nameCn: item.nameCn,
              nameEn: item.nameEn,
              employeeNumber: item.employeeNumber,
              companyCode: item.companyCode,
              mail: item.mail,
              isDelete: item.isDelete ? t('common.yes') : t('common.no')
            })
          )
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      getUploadUri() {
        return excelSupplierFileUpload()
      },
      getUploadHeader() {
        return { Authorization: useStore().state.user.token }
      },
      checkFile(file: any) {
        if (file) {
          console.log('file type')
          console.log(file)
        }
      },
      async handleAvatarSuccess(response: RootObject<number>, file: any) {
        if (response.data > 0) {
          ElMessage({
            message: t('fileUpload.uploadSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(null, 10)
        } else if (response.msg && response.msg !== 'success') {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      },
      handleAvatarError(response: RootObject<number>, file: any) {
        if (response.msg) {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const handleAppend = () => {
      router.push({ name: 'EmployeeCreate' })
    }
    const handleEdit = (row: EmployeeTableViewModel) => {
      router.push({ path: `employeeUpdate/${row.id}` })
    }
    const handleExport = async() => {
      const urlBlob = await exportEmployeeTemplate()
      if (urlBlob) {
        const link = document.createElement('a')
        link.href = urlBlob
        link.download = '员工模板.xlsx'
        link.click()
        window.URL.revokeObjectURL(urlBlob)
      } else {
        ElMessage({
          message: '获取excel失败',
          type: 'error',
          duration: 2000
        })
      }
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[3].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[3].options = optionList
      dataMap.searchFormStruct[3].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    onActivated(async() => {
      if (history.state?.refresh === true) {
        await dataMap.getPageList()
        // 用完删除
        const state = { ...history.state }
        delete state.refresh
        history.replaceState(state, '')
      }
    })
    return {
      t,
      handleAppend,
      handleExport,
      handleEdit,
      ...toRefs(dataMap)
    }
  }
})
